import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Modal, Platform } from 'react-native';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient, GradientOverlay } from './Gradients';
import GradientButton from './GradientButton';
import GradientText from './GradientText';
import { joinWaitlist } from '../services/launchService';
import { showToast } from '../utils/toast';

export default function CommunityModal({ visible, onClose }) {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    
    // Debug logging
    // console.log('CommunityModal render - visible:', visible);

    const handleJoin = async () => {
        if (!email.trim() || !email.includes('@')) return;
        
        setIsSubmitting(true);
        try {
            await joinWaitlist(email.trim(), 'community');
            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                setEmail('');
                onClose();
            }, 2000);
        } catch (error) {
            // Never show success for a failed save.
            showToast(error?.message || 'Could not join the waitlist. Try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        // console.log('CommunityModal: handleClose called');
        setEmail('');
        setShowSuccess(false);
        onClose();
    };

    if (!visible) return null;

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={handleClose}
        >
            <TouchableOpacity 
                style={styles.modalOverlay}
                activeOpacity={1}
                onPress={handleClose}
            >
                <BlurView intensity={60} tint="dark" style={styles.modalBlur}>
                    <TouchableOpacity 
                        activeOpacity={1}
                        onPress={(e) => e.stopPropagation()}
                        style={styles.modalContainer}
                    >
                        <GlassBackgroundGradient borderRadius={20} />
                        <GradientOverlay opacity={0.3} borderRadius={20} />
                        
                        <View style={styles.modalContent}>
                            {showSuccess ? (
                                <View style={styles.successContainer}>
                                    <GradientText style={styles.successTitle}>
                                        ✅ You're on the list!
                                    </GradientText>
                                    <Text style={styles.successDescription}>
                                        We'll notify you as soon as our community features launch.
                                    </Text>
                                </View>
                            ) : (
                                <>
                                    <View style={styles.modalHeader}>
                                        <GradientText style={styles.modalTitle}>
                                            Community is coming soon
                                        </GradientText>
                                        <Text style={styles.modalDescription}>
                                            Be the first to know when our community features launch! 
                                            Join our waitlist to get early access.
                                        </Text>
                                    </View>

                                    <View style={styles.inputContainer}>
                                        <TextInput
                                            style={styles.emailInput}
                                            value={email}
                                            onChangeText={setEmail}
                                            placeholder="you@email.com"
                                            placeholderTextColor="rgba(255,255,255,0.5)"
                                            keyboardType="email-address"
                                            autoCapitalize="none"
                                            autoCorrect={false}
                                            editable={!isSubmitting}
                                        />
                                    </View>

                                    <View style={styles.buttonContainer}>
                                        <GradientButton
                                            text={isSubmitting ? "Joining..." : "Join Waitlist"}
                                            onPress={handleJoin}
                                            style={styles.joinButton}
                                            disabled={isSubmitting || !email.trim() || !email.includes('@')}
                                        />
                                        <TouchableOpacity
                                            onPress={(e) => {
                                                e.stopPropagation();
                                                console.log('CommunityModal: Close button pressed');
                                                handleClose();
                                            }}
                                            style={styles.closeButton}
                                            disabled={isSubmitting}
                                        >
                                            <Text style={styles.closeButtonText}>Close</Text>
                                        </TouchableOpacity>
                                    </View>
                                </>
                            )}
                        </View>
                    </TouchableOpacity>
                </BlurView>
            </TouchableOpacity>
        </Modal>
    );
}

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalBlur: {
        width: '100%',
        maxWidth: 400,
        borderRadius: 20,
        overflow: 'hidden',
    },
    modalContainer: {
        position: 'relative',
        padding: 32,
        minHeight: 300,
    },
    modalContent: {
        position: 'relative',
        zIndex: 2,
    },
    modalHeader: {
        marginBottom: 24,
        alignItems: 'center',
    },
    modalTitle: {
        fontSize: 24,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 12,
    },
    modalDescription: {
        fontSize: 16,
        color: 'rgba(255, 255, 255, 0.8)',
        textAlign: 'center',
        lineHeight: 22,
    },
    inputContainer: {
        marginBottom: 24,
    },
    emailInput: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: 12,
        paddingHorizontal: 16,
        paddingVertical: 14,
        fontSize: 16,
        color: 'white',
        textAlign: 'center',
    },
    buttonContainer: {
        gap: 12,
    },
    joinButton: {
        height: 48,
    },
    closeButton: {
        height: 48,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    closeButtonText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 16,
        fontWeight: '500',
    },
    successContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
    },
    successTitle: {
        fontSize: 24,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 12,
    },
    successDescription: {
        fontSize: 16,
        color: 'rgba(255, 255, 255, 0.8)',
        textAlign: 'center',
        lineHeight: 22,
    },
});
